import React, { useState, useEffect } from 'react';
import { useUser, UserButton, useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import './LoginSuccess.css';
import cadscribeLogo from '../assets/cadscribe.png';
import config from '../config';

const LoginSuccess = () => {
  const { isLoaded, user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentLoaded, setContentLoaded] = useState(false);

  // Static ref to track if registration has been attempted
  const registrationRef = React.useRef(false);

  // Check if we're coming from a Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasStripeParams = urlParams.has('session_id');
    if (hasStripeParams) {
      console.log("Detected Stripe redirect - removing query parameters");
      // Remove query parameters from URL to avoid issues on refresh
      window.history.replaceState(null, '', '/login-success');
    }

    // Set content loaded to true after component mounts to prevent layout shifts
    setContentLoaded(true);
  }, []);

  useEffect(() => {
    // This prevents React Strict Mode double execution and ensures we only register once
    if (registrationRef.current) {
      console.log("Registration already attempted, skipping duplicate call");
      return;
    }

    // Automatically redirect to dashboard if coming from a previous session
    const autoRedirect = sessionStorage.getItem('authenticated');
    if (autoRedirect === 'true' && isLoaded && user) {
      console.log("User already authenticated, redirecting to dashboard");
      navigate('/home', { replace: true });
      return;
    }

    const registerWithBackend = async () => {
      // Skip if not loaded or no user
      if (!isLoaded || !user) return;

      // Set flag BEFORE attempting registration to prevent race conditions
      registrationRef.current = true;
      console.log("Setting registration flag to prevent duplicate calls");

      try {
        // Get session token from Clerk - without any template
        const token = await getToken();
        console.log("Got Clerk token for API authentication");
        console.log(`Token preview: ${token ? token.substring(0, 10) + '...' : 'undefined'}`);

        // Send token to backend
        console.log(`Sending auth request to backend...`);
        // Use simple relative path for maximum compatibility
        const apiUrl = `/api/clerk/authorize`;
        console.log(`Using API URL: ${apiUrl}`);

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          let errorText = 'Authentication failed';
          try {
            const errorData = await response.json();
            errorText = errorData.error || errorText;
          } catch (e) {
            errorText = await response.text() || errorText;
          }
          console.error(`Auth request failed: ${response.status} - ${errorText}`);
          throw new Error(errorText);
        }

        const data = await response.json();
        console.log("Received user data from backend:", data);
        setUserData(data);
        setLoading(false);
        
        // Mark as authenticated for future navigations
        sessionStorage.setItem('authenticated', 'true');
      } catch (err) {
        console.error('Authentication error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    // Only register if loaded and we have a user
    if (isLoaded && user) {
      console.log("Initiating backend registration");
      registerWithBackend();
    } else if (isLoaded && !user) {
      console.log("No user found, redirecting to login");
      setError("No user found. Please sign in again.");
      setLoading(false);
    }
  }, [isLoaded, user, getToken, navigate]);

  return (
    <div className="login-success-container">
      <div className="dot-mask"></div>
      <header className="login-success-header">
        <img src={cadscribeLogo} alt="CADscribe Logo" className="login-success-logo" />
        <div className="user-button-container">
          <UserButton />
        </div>
      </header>

      <main className={`login-success-main ${contentLoaded ? 'loaded' : 'hidden'}`}>
        {loading || !isLoaded ? (
          <div className="loading">Loading...</div>
        ) : error ? (
          <div className="error">
            <h1>Authentication Error</h1>
            <p>{error}</p>
            <button onClick={() => navigate('/')} className="back-btn">Back to Login</button>
          </div>
        ) : userData ? (
          <>
            <h1>Welcome, {userData.given_name || user.firstName || user.username || 'User'}!</h1>
            <p>You have successfully logged in to CADscribe.</p>
              <div className="user-info">
                <p>Email: {userData.email || user.primaryEmailAddress?.emailAddress}</p>
                <p>Free messages: {userData.free_messages || 0}</p>
              </div>
              <button 
                onClick={() => {
                  const button = document.querySelector('.continue-btn');
                  if (button) {
                    button.textContent = 'Redirecting...';
                    button.disabled = true;
                  }
                  navigate('/home', { replace: true });
                }} 
                className="continue-btn"
              >
                Continue to Dashboard
              </button>
          </>
        ) : (
          <div className="error">
            <h1>Login Error</h1>
            <p>Could not retrieve user information. Please try logging in again.</p>
            <button onClick={() => navigate('/')} className="back-btn">Back to Login</button>
          </div>
        )}
      </main>

      <footer className="login-success-footer">
        <p>&copy; {new Date().getFullYear()} CADscribe. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LoginSuccess;